import { useFormikContext } from 'formik'

import { getNumberOfUnitsInLot } from '@b-stock/fe-core/bstock-next/helpers'
import type {
  OrchestratedOfferingListingDetails,
  OrchestratedQuote,
} from '@b-stock/fe-core/bstock-next/queries'
import { centsToDollars } from '@b-stock/fe-core/bstock-react'

import { calculateFeePercent } from '@/buyer-portal/components/features/listing/hooks/calculateFeePercent'
import { useCalculatedAdditionalCharges } from '@/buyer-portal/components/features/listing/hooks/useCalculatedAdditionalCharges'
import { useSuppressShippingLine } from '@/buyer-portal/components/features/listing/hooks/useSuppressShippingLine'
import { useTlcTotals } from '@/buyer-portal/components/features/listing/hooks/useTlcTotals'
import activeSubscriptionsQuery from '@/buyer-portal/queries/activeSubscriptionsQuery'

import type { MakeAnOfferFormValues } from './SimpleOfferActionModule/MakeAnOfferFormProvider'

/**
 * Total landed cost for the offer in play. Shared so the card's TLC display and
 * the `confirm_offer` analytics can't report different numbers.
 */
export const useOfferTlc = (
  listing: OrchestratedOfferingListingDetails,
  selectedQuote?: OrchestratedQuote | null
) => {
  const { currency = 'USD', lot } = listing
  const { values } = useFormikContext<MakeAnOfferFormValues>()

  const { data: sellerSubscription } = activeSubscriptionsQuery.useQuery(
    listing.accountId
  )

  const offerCents =
    values.amount || values.currentOfferAmount || values.minOfferAmount
  const currentBidDollars = centsToDollars(offerCents)

  const additionalCharges = useCalculatedAdditionalCharges({
    buyersPremium: sellerSubscription?.buyersPremium ?? 0,
    currentBidDollars,
    selectedQuote,
  })

  const { basis, bstockFee, isWithoutShipping } = additionalCharges
  const suppressShippingLine = useSuppressShippingLine(listing, selectedQuote)
  const feePercent = calculateFeePercent(bstockFee, basis)
  const msrpPrice = lot.retailValue
  const units = getNumberOfUnitsInLot(lot) ?? 0

  const tlcTotals = useTlcTotals({
    additionalCharges,
    currency,
    listingId: listing._id,
    listingType: listing.listingType,
    msrpPrice,
    selectedQuote,
    units,
  })

  return {
    ...tlcTotals,
    basis,
    bstockFee,
    feePercent,
    isWithoutShipping,
    suppressShippingLine,
    msrpPrice,
    units,
  }
}
